import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { formatCurrency } from '@/lib/utils';
import assetService from '@/services/AssetService';
import FinancialService, {
  type FinancialRecord,
  type FinancialSuggestion,
} from '@/services/FinancialService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, Check, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CreateFinancialForm } from './forms/CreateFinancialForm';
import { EditFinancialForm } from './forms/EditFinancialForm';

interface AssetFinancialsProps {
  assetId: string;
  openAddModal?: boolean;
  initialCategory?: string;
}

interface FinancialCardProps {
  financial: FinancialRecord;
  isActive: boolean;
  onSelect: (financial: FinancialRecord) => void;
  onDelete: (financialId: number) => void;
  onEditSuccess: () => void;
  onEditCancel: () => void;
}

interface FinancialSuggestionCardProps {
  suggestion: FinancialSuggestion;
  onCreateFromSuggestion: (suggestion: FinancialSuggestion) => void;
}

function FinancialSuggestionCard({
  suggestion,
  onCreateFromSuggestion,
}: FinancialSuggestionCardProps) {
  return (
    <div className="p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium">{suggestion.category}</h4>
          <p className="text-sm text-muted-foreground">
            {suggestion.suggestedFrequency} • {suggestion.transactionCount}{' '}
            transactions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`font-medium ${
              suggestion.type === 'INCOME' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {suggestion.type === 'INCOME' ? '+' : '-'}
            {formatCurrency(suggestion.suggestedAmount)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCreateFromSuggestion(suggestion)}
          >
            <Check className="h-4 w-4 mr-1" />
            Create
          </Button>
        </div>
      </div>

      {suggestion.sampleTransactions.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <p className="mb-1">Sample transactions:</p>
          <div className="space-y-1">
            {suggestion.sampleTransactions.map((txn) => (
              <div key={txn.id} className="flex justify-between">
                <span>{txn.name}</span>
                <span>{formatCurrency(txn.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FinancialCard({
  financial,
  isActive,
  onSelect,
  onDelete,
  onEditSuccess,
  onEditCancel,
}: FinancialCardProps) {
  const [isBackfilling, setIsBackfilling] = useState(false);
  const queryClient = useQueryClient();

  // Check for missing transactions in YTD
  const { data: missingTransactionsCount } = useQuery({
    queryKey: ['missing-transactions', financial.asset_id, financial.id],
    queryFn: async () => {
      if (financial.frequency === 'One-time') return 0;

      try {
        // Calculate date range for checking missing transactions (YTD)
        const endDate = new Date();
        const startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of current year

        const response = await FinancialService.getMissingTransactions(
          financial.asset_id,
          financial.id,
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0],
        );
        return response.count;
      } catch (error) {
        console.error('Error checking missing transactions:', error);
        return 0;
      }
    },
    enabled: financial.frequency !== 'One-time',
  });

  // Determine if we should show the backfill button
  const shouldShowBackfill =
    financial.frequency !== 'One-time' &&
    missingTransactionsCount !== undefined &&
    missingTransactionsCount > 0;

  // Determine what date information to show
  const getDateDisplay = () => {
    if (financial.frequency === 'One-time') {
      return `Date: ${financial.due_dates?.[0] || 'Not set'}`;
    }
    return `Next: ${financial.due_dates?.[0] || 'Not set'}`;
  };

  const handleBackfill = async () => {
    if (financial.frequency === 'One-time') {
      toast.error('Cannot backfill one-time financials');
      return;
    }

    setIsBackfilling(true);
    try {
      // Calculate date range for backfilling (YTD)
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of current year

      // Store the count before backfilling
      const countBeforeBackfill = missingTransactionsCount || 0;

      await FinancialService.backfillTransactions(
        financial.asset_id,
        financial.id,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
      );

      if (countBeforeBackfill > 0) {
        console.log(
          'Showing success toast for backfill with count:',
          countBeforeBackfill,
        );
        toast.success(
          `Successfully backfilled ${countBeforeBackfill} missing transactions`,
        );
      } else {
        console.log('Showing success toast for backfill with no transactions');
        toast.success('Backfill completed - no missing transactions found');
      }
      // Refresh transactions data
      queryClient.invalidateQueries({
        queryKey: ['transactions', financial.asset_id],
      });
      // Refresh missing transactions count
      queryClient.invalidateQueries({
        queryKey: ['missing-transactions', financial.asset_id, financial.id],
      });
    } catch (error) {
      console.error('Error backfilling transactions:', error);
      toast.error('Failed to backfill transactions');
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <div
      className="p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
      onKeyUp={() => onSelect(financial)}
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">
            {financial.details || financial.category}
          </h4>
          <p className="text-sm text-muted-foreground">
            {financial.frequency} • {getDateDisplay()}
            {shouldShowBackfill && missingTransactionsCount && (
              <span className="ml-2 text-orange-600">
                • {missingTransactionsCount} missing
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`font-medium ${
              financial.type === 'INCOME' ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {financial.type === 'INCOME' ? '+' : '-'}
            {formatCurrency(financial.amount)}
          </span>
          {shouldShowBackfill && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleBackfill();
              }}
              disabled={isBackfilling}
            >
              <Calendar className="h-4 w-4 mr-1" />
              {isBackfilling ? 'Backfilling...' : 'Backfill'}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(financial.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isActive && (
        <div
          className="mt-4 pt-4 border-t"
          onKeyUp={(e) => e.stopPropagation()}
        >
          <EditFinancialForm
            assetId={financial.asset_id}
            financial={financial}
            onSuccess={onEditSuccess}
            onCancel={onEditCancel}
          />
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );
}

// Helper function to format date for display
const formatDateForDisplay = (dateValue: string | null | undefined): string => {
  if (!dateValue) return 'Not set';

  try {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Not set';

    return date.toLocaleDateString();
  } catch {
    return 'Not set';
  }
};

export function AssetFinancialsTab({
  assetId,
  openAddModal,
  initialCategory,
}: AssetFinancialsProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [activeFinancial, setActiveFinancial] =
    useState<FinancialRecord | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const queryClient = useQueryClient();

  const { data: financials, isLoading } = useQuery({
    queryKey: ['financials', assetId],
    queryFn: () => FinancialService.getAssetFinancials(assetId),
  });

  // Get asset data for display
  const { data: asset } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => assetService.getAsset(assetId),
  });

  // Get financial suggestions
  const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery({
    queryKey: ['financial-suggestions', assetId],
    queryFn: () => FinancialService.analyzeTransactionsForFinancials(assetId),
    enabled: showSuggestions,
  });

  // Separate financials into recurring and one-time
  const recurringFinancials =
    financials?.filter((financial) => financial.frequency !== 'One-time') || [];

  const oneTimeFinancials =
    financials?.filter((financial) => financial.frequency === 'One-time') || [];

  const handleFinancialClick = (financial: FinancialRecord) => {
    if (activeFinancial?.id === financial.id) {
      setActiveFinancial(null);
    } else {
      setActiveFinancial(financial);
    }
  };

  const handleDelete = async (financialId: number) => {
    try {
      await FinancialService.deleteFinancial(assetId, financialId);
      toast.success('Financial record deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['financials', assetId] });
      setActiveFinancial(null);
    } catch (error) {
      console.error('Error deleting financial record:', error);
      toast.error('Failed to delete financial record');
    }
  };

  const handleCreateSuccess = () => {
    setIsCreateOpen(false);
    queryClient.invalidateQueries({ queryKey: ['financials', assetId] });
    // Refresh suggestions after creating a financial
    if (showSuggestions) {
      queryClient.invalidateQueries({
        queryKey: ['financial-suggestions', assetId],
      });
    }
  };

  // Effect to open add modal if prop is true
  useEffect(() => {
    if (openAddModal) {
      setIsCreateOpen(true);
    }
  }, [openAddModal]);

  const handleEditSuccess = () => {
    setActiveFinancial(null);
    queryClient.invalidateQueries({ queryKey: ['financials', assetId] });
  };

  const handleCreateFromSuggestion = async (
    suggestion: FinancialSuggestion,
  ) => {
    try {
      const newFinancial = {
        amount: suggestion.suggestedAmount,
        frequency: suggestion.suggestedFrequency,
        type: suggestion.type,
        category: suggestion.category,
        is_percent: false,
        notify: false,
        due_dates: [new Date().toISOString().split('T')[0]], // Set to today
        details: `${suggestion.category}`,
      };

      await FinancialService.createFinancial(assetId, newFinancial);
      toast.success(
        `Financial record created: ${suggestion.category} - ${formatCurrency(suggestion.suggestedAmount)}`,
      );

      // Refresh financials data
      queryClient.invalidateQueries({ queryKey: ['financials', assetId] });

      // Hide suggestions after creating a financial
      setShowSuggestions(false);

      // Refresh suggestions data (in case user reopens suggestions)
      queryClient.invalidateQueries({
        queryKey: ['financial-suggestions', assetId],
      });
    } catch (error) {
      console.error('Error creating financial from suggestion:', error);
      toast.error('Failed to create financial record');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Financials</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Purchase & Loan Details Display */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase & Loan Details</CardTitle>
        </CardHeader>
        <CardContent>
          {asset ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Purchase Information Display */}
              <div className="space-y-4">
                <h4 className="font-medium">Purchase Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Purchase Date:
                    </span>
                    <span>{formatDateForDisplay(asset.purchase_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Purchase Price:
                    </span>
                    <span>
                      {asset.purchase_price
                        ? formatCurrency(asset.purchase_price)
                        : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Down Payment:</span>
                    <span>
                      {asset.down_payment
                        ? formatCurrency(asset.down_payment)
                        : 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Loan Information Display */}
              <div className="space-y-4">
                <h4 className="font-medium">Loan Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Has Mortgage:</span>
                    <span>
                      {asset.loan_amount !== null &&
                      asset.loan_amount !== undefined &&
                      asset.loan_amount > 0
                        ? 'Yes'
                        : 'No'}
                    </span>
                  </div>
                  {asset.loan_amount !== null &&
                    asset.loan_amount !== undefined &&
                    asset.loan_amount > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Loan Amount:
                          </span>
                          <span>
                            {asset.loan_amount
                              ? formatCurrency(asset.loan_amount)
                              : 'Not set'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Loan Due Date:
                          </span>
                          <span>
                            {formatDateForDisplay(asset.loan_due_date)}
                          </span>
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              Loading asset details...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recurring Financials */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recurring Financials</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSuggestions(!showSuggestions)}
            >
              <Search className="h-4 w-4 mr-2" />
              {showSuggestions ? 'Hide' : 'Scan'} Suggestions
            </Button>
            <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <SheetTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Financial
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Add Financial Record</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <CreateFinancialForm
                    assetId={assetId}
                    onSuccess={handleCreateSuccess}
                    onCancel={() => setIsCreateOpen(false)}
                    initialCategory={initialCategory}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </CardHeader>
        <CardContent>
          {showSuggestions && (
            <div className="mb-6 p-4 border rounded-lg bg-muted/30">
              {suggestionsLoading ? (
                <div className="text-center py-4">
                  <LoadingSpinner />
                  <p className="text-muted-foreground mt-2">
                    Analyzing transactions...
                  </p>
                </div>
              ) : suggestionsData?.suggestions &&
                suggestionsData.suggestions.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Found {suggestionsData.suggestions.length} potential
                      financial records from {suggestionsData.totalTransactions}{' '}
                      transactions
                    </p>
                  </div>
                  {suggestionsData.suggestions.map((suggestion, index) => (
                    <FinancialSuggestionCard
                      key={index}
                      suggestion={suggestion}
                      onCreateFromSuggestion={handleCreateFromSuggestion}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  No financial suggestions found. Add more transactions to get
                  suggestions.
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            {recurringFinancials.map((financial) => (
              <FinancialCard
                key={financial.id}
                financial={financial}
                isActive={activeFinancial?.id === financial.id}
                onSelect={handleFinancialClick}
                onDelete={handleDelete}
                onEditSuccess={handleEditSuccess}
                onEditCancel={() => setActiveFinancial(null)}
              />
            ))}

            {recurringFinancials.length === 0 && !showSuggestions && (
              <div className="text-center py-8 text-muted-foreground">
                No recurring financial records found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* One-time Financials */}
      <Card>
        <CardHeader>
          <CardTitle>One-time Financials</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {oneTimeFinancials.map((financial) => (
              <FinancialCard
                key={financial.id}
                financial={financial}
                isActive={activeFinancial?.id === financial.id}
                onSelect={handleFinancialClick}
                onDelete={handleDelete}
                onEditSuccess={handleEditSuccess}
                onEditCancel={() => setActiveFinancial(null)}
              />
            ))}

            {oneTimeFinancials.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No one-time financial records found
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
